import { useUserStore } from '@/store/user.store'
import type { AnswerAnalysis, XFWord, XFWordsData } from '@/views/common/TopicPage/api/index.api'
import ClipboardJS from 'clipboard'
import { ElMessageBox, ElNotification } from 'element-plus'

import { useWordsColor } from '@/store/wordsColor.store'

/**
 * 根据秒获取 00:00的时间格式
 * @param seconds --- 秒
 */

export function secondsToTime(seconds: number) {
  var minutes = Math.floor(seconds / 60)
  if (minutes < 0) {
    minutes = 0
  }
  var remainingSeconds = Math.floor(seconds % 60) // 四舍五入到整数秒
  if (remainingSeconds < 0) {
    remainingSeconds = 0
  }
  var formattedSeconds = remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds
  return minutes + ':' + formattedSeconds
}

/**
 * 获取时间距离当前时间间隔文本
 * @param dateTimeStamp 时间
 * @returns
 */
export function getDateDiff(dateTimeStamp: Date) {
  // 时间字符串转时间戳
  var timestamp = new Date(dateTimeStamp).getTime()

  var minute = 1000 * 60
  var hour = minute * 60
  var day = hour * 24
  var month = day * 30
  var year = day * 365
  var now = new Date().getTime()
  var diffValue = now - timestamp
  var result: any
  if (diffValue < 0) {
    return
  }
  var yearC = diffValue / year
  var monthC = diffValue / month
  var weekC = diffValue / (7 * day)
  var dayC = diffValue / day
  var hourC = diffValue / hour
  var minC = diffValue / minute
  if (yearC >= 1) {
    result = '' + parseInt(yearC.toString()) + '年前'
  } else if (monthC >= 1) {
    result = '' + parseInt(monthC.toString()) + '月前'
  } else if (weekC >= 1) {
    result = '' + parseInt(weekC.toString()) + '周前'
  } else if (dayC >= 1) {
    result = '' + parseInt(dayC.toString()) + '天前'
  } else if (hourC >= 1) {
    result = '' + parseInt(hourC.toString()) + '小时前'
  } else if (minC >= 1) {
    result = '' + parseInt(minC.toString()) + '分钟前'
  } else result = '刚刚'
  return result
}

/**
 * 单词文本JSON化 ${单词}
 * @param text 单词文本
 * @returns
 */
export function parseText(text: string) {
  if (!text) return []
  const regex = /(\$\{[^}]+\}|[A-Za-z0-9']+|[^A-Za-z0-9'\s]+)/g
  const matches = text.match(regex)
  const result: {
    type: 'word' | 'symbol' | 'special_word'
    value: string
  }[] = []
  if (!matches) return result
  matches.forEach((item) => {
    if (item.startsWith('${') && item.endsWith('}')) {
      // If the item is a word in curly braces
      let cleanItem = item.replace('${', '').replace('}', '')
      result.push({ type: 'special_word', value: cleanItem })
    } else if (/^[A-Za-z0-9']+$/.test(item)) {
      // If the item is a word
      result.push({ type: 'word', value: item })
    } else {
      // If the item is a symbol
      result.push({ type: 'symbol', value: item })
    }
  })
  return result
}

/**
 * 单词文本JSON化 ${单词}
 * @param text 单词文本
 * @returns
 */
export function parseConjunction(text: string) {
  if (!text) return []
  // 修改正则表达式，允许单词中包含连字符（-）
  const regex = /(\$\{[^}]+\}|[A-Za-z0-9'-]+|[^A-Za-z0-9'\s]+)/g
  const matches = text.match(regex)
  const result: {
    type: 'word' | 'symbol' | 'special_word'
    value: string
  }[] = []
  if (!matches) return result
  matches.forEach((item) => {
    if (item.startsWith('${') && item.endsWith('}')) {
      const cleanItem = item.slice(2, -1) // 更高效的清理方式
      result.push({ type: 'special_word', value: cleanItem })
    } else if (/^[A-Za-z0-9'-]+$/.test(item)) {
      // 同步更新正则校验
      result.push({ type: 'word', value: item })
    } else {
      result.push({ type: 'symbol', value: item })
    }
  })
  return result
}

/**
 *适用于 听力题型....
 * @param text  文本json化
 * @returns TextToJson[]
 */
export const formatTextToJson = (text: string) => {
  const list: TextToJson[] = []
  const testJson = parseConjunction(text)
  testJson.map((item, index, arr) => {
    if (item.type === 'word') {
      list.push({
        text: item.value,
        type: item.type,
        texts: [item.value],
        answer: ''
      })
    }
    if (item.type === 'symbol') {
      list.push({
        text: item.value,
        type: item.type,
        texts: [item.value],
        answer: ''
      })
    }
    if (item.type === 'special_word') {
      list.push({
        text: item.value,
        type: item.type,
        texts: item.value.split('|'),
        answer: ''
      })
      list.push({
        text: ' ',
        type: 'blank',
        texts: [],
        answer: ''
      })
    }
    // 连接符不需要加空格处理
    if (arr[index].value == '—' || arr[index].value === '-') return
    if (
      (arr[index + 1] && arr[index].type === 'word' && arr[index + 1].type !== 'symbol') ||
      (arr[index + 1] && arr[index].type === 'symbol' && arr[index + 1].type !== 'symbol')
    ) {
      list.push({
        text: ' ',
        type: 'blank',
        texts: [],
        answer: ''
      })
    }
  })
  return list
}
export interface TextToJson {
  text: string
  type: 'word' | 'symbol' | 'special_word' | 'blank'
  texts: string[]
  answer: string
}

/**
 * 生成练习记结果录评测
 * @param records
 * @returns HTML string
 */
export const renderwriteRecords = (records: AnswerAnalysis[]) => {
  var recordStr = ''
  var regPunctuation =
    /[\ |\~|\`|\!|\@|\#|\$|\%|\^|\&|\*|\(|\)|\-|\_|\+|\=|\||\\|\[|\]|\{|\}|\;|\:|\"|\'|\,|\<|\.|\>|\/|\?]/g

  records.forEach(function (V, K) {
    if (regPunctuation.test(V.key)) {
      recordStr += '<span style="margin-left:0;">' + V.key + '</span> '
      return
    }
    if (V.value === 0) {
      recordStr +=
        '<span style="color:#808080;text-decoration: line-through;">' + V.key + '</span> '
    } else if (V.value === 1) {
      recordStr += '<span style="color:#6ead5c;">' + V.key + '</span> '
    } else if (V.value === 2) {
      var text = V.key
      if (K === 0) {
        text = '(' + text
      } else if (records[K - 1].value !== 2) {
        text = '(' + text
      }
      if (K === records.length - 1) {
        text = text + ')'
      } else if (records[K + 1].value !== 2) {
        text = text + ')'
      }
      recordStr += '<span style="color:#e42104;font-weight: bold;">' + text + '</span> '
    }
  })
  return recordStr
}
/**
 *
 * @param count 将万以上的数据转成小数显示 10000=1w
 * @returns
 */
export const playCountFormat = (count?: number) => {
  if (!count) {
    return '0次'
  }
  if (count < 10000) {
    return count + '次'
  } else {
    return Math.round(count / 1000) / 10 + 'W次'
  }
}

/**
 *
 * @param audioId 音频id
 * @param qId 题目id
 */
export const shareResult = (audioId: number | string, qId: number, shareType: number = 0) => {
  console.log(audioId, qId)

  const url = `${
    window.location.origin || 'https://www.ytaxx.com'
  }/appPage/index.html?id=${audioId}&questionId=${qId}&shareType=${shareType}`
  const clipboard = new ClipboardJS('#copy-result', {
    text: function () {
      return url
    }
  })
    .on('success', function () {
      ElNotification({
        message: '复制成功',
        type: 'success'
      })
      clipboard.destroy()
    })
    .on('error', function () {
      ElNotification({
        message: '复制失败,请手动复制',
        type: 'error'
      })
      ElMessageBox.alert(url, {
        title: '分享结果',
        confirmButtonText: 'OK'
      })
      clipboard.destroy()
    })
}

/**
 *
 * @param audioId 音频id
 * @param qId 题目id
 */
export const shareRaResult = (
  audioId: number | string,
  qId: number | string,
  shareType?: number | string
) => {
  const url = `${
    window.location.origin
  }/appPage/share/RA/index.html?id=${BigInt(audioId).toString()}&questionId=${qId}&shareType=${shareType || 0}`
  const clipboard = new ClipboardJS('#copy-result', {
    text: function () {
      return url
    }
  })
    .on('success', function () {
      ElNotification({
        message: '复制成功',
        type: 'success'
      })
      clipboard.destroy()
    })
    .on('error', function () {
      ElNotification({
        message: '复制失败,请手动复制',
        type: 'error'
      })
      ElMessageBox.alert(url, {
        title: '分享结果',
        confirmButtonText: 'OK'
      })
      clipboard.destroy()
    })
}

export const percentage90 = (number?: number) => {
  if (!number) {
    return 0
  }
  return (number / 90) * 100
}
export function getQueryString(name: string) {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
  let r: RegExpMatchArray | null | string = decodeURI(window.location.search)
  r = r.substr(1).match(reg)
  if (r != null) return unescape(r[2])
  return null
}

export const toVip = (href: string) => {
  const user = useUserStore()
  const otherWindow = window.open(href)
  if (otherWindow) {
    // 接受标签页信息
    otherWindow.onmessage = (data) => {
      if (data.data.type === 'vip') {
        user.getUserInfo() //开通vip后重新更新用户信息
      }
    }
  }
}

export const dealSearchText = (text: string, index: number, key?: string) => {
  let originalText = text
  if (!key) {
    return originalText
  }
  let start = originalText.toUpperCase().indexOf(key.toUpperCase())
  if (index != -1) {
    originalText = originalText.substring(Math.max(0, start - index), originalText.length)
  }
  let tempText = text
  const hilightWord: string[] = []
  while (tempText.toUpperCase().indexOf(key.toUpperCase()) >= 0) {
    let start = tempText.toUpperCase().indexOf(key.toUpperCase())
    hilightWord.push(tempText.substring(start, start + key.length))
    tempText = tempText.substring(start + key.length, tempText.length)
  }
  hilightWord.forEach((word) => {
    originalText = originalText.replace(word, (originalText) => {
      return `<b>${originalText}</b>`
    })
  })
  return originalText
}

export const formatXfWords = (xfWords: string) => {
  const wordsColorStore = useWordsColor()
  var Regx = /^[A-Za-z0-9]/
  if (!xfWords) return []
  try {
    let result = JSON.parse(xfWords) as XFWordsData[]
    const newArr = result[0].words.map((word) => {
      // 分数颜色
      const result = wordsColorStore.wordsColor.find((item: any) => {
        return word.score < item.end && word.score >= item.start
      })
      let newWord: XFWord = word
      newWord.style = {}
      // 是单词
      newWord.style['color'] = result?.color
      if (newWord.text) {
        if (newWord.text.indexOf('[漏读]') !== -1) {
          newWord.text = '(' + newWord.text.replace('[漏读]', '') + ')'
        } else if (newWord.text.indexOf('[增读]') !== -1) {
          newWord.text = newWord.text.replace('[增读]', '')
          if (newWord.style) {
            newWord.style['text-decoration'] = 'line-through'
          }
        } else {
          if (Regx.test(newWord.text)) {
            newWord.type = 'word'
          } else {
            newWord.style.color = '#000'
          }
        }
      }
      return newWord
    })
    return newArr
  } catch {
    return []
  }
}
export const formatWords = (words: string) => {
  if (!words) return null
  try {
    let result = JSON.parse(words) as XFWordsData
    return result
  } catch {
    return null
  }
}

export const scrollTitleView = () => {
  document.querySelector('.top-layout-content-box')?.scrollTo({
    left: 0,
    top: 131,
    behavior: 'smooth'
  })
}

import convert from 'xml-js'

export interface WordScore {
  score: number
  syllables: {
    score: number
    syls: SyllableItem[]
  }[]
  audioUrl?: string
  /**
   * 音频开始时间
   */
  st?: number
  /**
   * 音频开始时间
   */
  end?: number
}

interface SyllableItem {
  /**0正常其余看文档 */
  type: 0 | 16 | 32 | 64 | 128
  text: string
}

/**
 * 音节对照表
 * @param key 音节字母
 * @returns
 */
export const getLetter = (key: string) => {
  const phonemeToLetterMap = new Map([
    ['aa', 'bː'],
    ['ɑː', 'f'],
    ['f', 'f'],
    ['ae', 'æ'],
    ['g', 'g'],
    ['ah', 'ʌ'],
    ['hh', 'h'],
    ['ao', 'ɔː'],
    ['jh', 'dʒ'],
    ['ar', 'eə'],
    ['k', 'k'],
    ['aw', 'aʊ'],
    ['l', 'l'],
    ['ax', 'ə'],
    ['m', 'm'],
    ['ay', 'aɪ'],
    ['n', 'n'],
    ['eh', 'e'],
    ['ng', 'ŋ'],
    ['er', 'ɜː'],
    ['p', 'p'],
    ['ey', 'eɪ'],
    ['r', 'r'],
    ['ih', 'ɪ'],
    ['s', 's'],
    ['ir', 'ɪə'],
    ['sh', 'ʃ'],
    ['iy', 'iː'],
    ['t', 't'],
    ['oo', 'ɒ'],
    ['th', 'θ'],
    ['v', 'v'],
    ['oy', 'ɒɪ'],
    ['w', 'w'],
    ['uh', 'ʊ'],
    ['y', 'j'],
    ['uw', 'uː'],
    ['z', 'z'],
    ['ur', 'ʊə'],
    ['zh', 'ʒ'],
    ['b', 'b'],
    ['dr', 'dr'],
    ['ch', 'tʃ'],
    ['dz', 'dz'],
    ['d', 'd'],
    ['tr', 'tr'],
    ['dh', 'ð'],
    ['ts', 'ts']
  ])
  return phonemeToLetterMap.get(key) || key
}

/**
 * 解析讯飞xml 获取单词评分分数和音节
 * @param xml 讯飞xml
 */
export function xfXmlParseToScore(xml: string) {
  try {
    const payload: WordScore = {
      score: 0,
      syllables: []
    }
    const result1 = JSON.parse(convert.xml2json(xml, { compact: true, spaces: 4 }))
    const xmlWord = result1?.xml_result?.read_sentence?.rec_paper?.read_chapter?.sentence?.word
    const word = (() => {
      if (Array.isArray(xmlWord)) {
        return xmlWord[0]
      } else {
        return xmlWord
      }
    })()

    if (!word) {
      throw new Error('')
    } else {
      if (word._attributes) {
        payload.score = Number(word?._attributes['total_score'] | 0) * 18 || 10
      }

      let syll = []
      if (Array.isArray(word.syll)) {
        syll.push(...word.syll)
      } else {
        syll.push(word.syll)
      }
      syll.forEach((item: any) => {
        const index = payload.syllables.push({
          score: item._attributes.syll_score,
          syls: []
        })

        if (item?.phone && Array.isArray(item.phone)) {
          item.phone.forEach((content: any) => {
            payload.syllables[index - 1].syls.push({
              text: getLetter(content._attributes.content || ''),
              type: content._attributes.dp_message
            })
          })
        } else {
          payload.syllables[index - 1].syls.push({
            text: getLetter(item?.phone?._attributes.content || ''),
            type: item?.phone?._attributes.dp_message
          })
        }
      })
      return payload
    }
  } catch (error) {
    console.error(error)
    return {
      score: 0,
      syllables: []
    } as WordScore
  }
}

/**
 * !!!! 注意 模考专用解析
 * 解析讯飞模考xml 获取单词评分分数和音节
 * @param xml 讯飞xml
 */
export function xfXmlMockParseToScore(xml: string) {
  try {
    const payload: WordScore = {
      score: 0,
      syllables: []
    }
    const result1 = JSON.parse(convert.xml2json(xml, { compact: true, spaces: 4 }))

    const xmlWord = result1?.xml_result?.read_word?.rec_paper?.read_word?.sentence?.word
    const word = (() => {
      if (Array.isArray(xmlWord)) {
        return xmlWord[0]
      } else {
        return xmlWord
      }
    })()

    if (!word) {
      throw new Error('')
    } else {
      if (word._attributes) {
        payload.score = Number(word?._attributes['total_score'] | 0) * 18 || 10
      }

      let syll = []
      if (Array.isArray(word.syll)) {
        syll.push(...word.syll)
      } else {
        syll.push(word.syll)
      }
      syll.forEach((item: any) => {
        const index = payload.syllables.push({
          score: item._attributes.syll_score,
          syls: []
        })

        if (item?.phone && Array.isArray(item.phone)) {
          item.phone.forEach((content: any) => {
            payload.syllables[index - 1].syls.push({
              text: getLetter(content._attributes.content || ''),
              type: content._attributes.dp_message
            })
          })
        } else {
          payload.syllables[index - 1].syls.push({
            text: getLetter(item?.phone?._attributes.content || ''),
            type: item?.phone?._attributes.dp_message
          })
        }
      })
      return payload
    }
  } catch (error) {
    console.error(error)
    return {
      score: 0,
      syllables: []
    } as WordScore
  }
}

/**
 *
 * @param val 输入内容
 */
export const formatInput = (val: string) => {
  return val.trimStart().replace(/[^\w;:'"|,.<>?~·!@#$%^&*()+\-—*/=\[\]{}/\\\s]/g, '')
}

export interface WordItem {
  word: string
  beg: number
  end: number
  score: number
  syllList: SyllList[]
}

interface SyllList {
  syll: string
  beg: number
  end: number
  score: number
  syllAccent: number
}
